import { type RefObject, useEffect } from 'react'

import type { NodeOverlay } from './canvasTypes'
import type { GraphLiveAppearance } from './graphInstrumentation'
import type { GraphNodeOverlayLayerProps } from './GraphNodeOverlayLayer'
import { useGraphNodePointerActivation } from './useGraphNodePointerActivation'
import {
  type NodeOverlayInspection,
  useNodeOverlayInspection,
} from './useNodeOverlayInspection'
import type { OpenOffPageConnector } from './useOpenOffPageConnector'

type GraphOverlaySessionOptions = {
  overlays: NodeOverlay[]
  liveValueAppearances: GraphLiveAppearance[]
  svgRef: RefObject<SVGSVGElement>
  containerRef: RefObject<HTMLDivElement>
  onBoxMouseLeave: () => void
  onAliasHover: (highlight: null) => void
  onDocReferenceHover?: (highlight: null) => void
  onOpenOffPageConnector?: OpenOffPageConnector
  onSelectFlowNode?: (overlay: NodeOverlay) => void
  onSelectNode: (overlay: NodeOverlay) => void
  selectedNodeId: string | undefined
}
type GraphOverlaySession = {
  nodeInspection: NodeOverlayInspection | null
  nodeOverlayLayer: GraphNodeOverlayLayerProps
  suspend: () => void
  dismiss: () => void
  isInspecting: boolean
}

export function useGraphOverlaySession({
  overlays,
  liveValueAppearances,
  svgRef,
  containerRef,
  onBoxMouseLeave,
  onAliasHover,
  onDocReferenceHover,
  onOpenOffPageConnector,
  onSelectFlowNode,
  onSelectNode,
  selectedNodeId,
}: GraphOverlaySessionOptions): GraphOverlaySession {
  const { inspection, activeNodeIds, suspend, dismiss } =
    useNodeOverlayInspection(
      overlays,
      svgRef,
      containerRef,
      liveValueAppearances
    )
  const isInspecting = inspection !== null
  useGraphNodePointerActivation({
    overlays,
    svgRef,
    containerRef,
    liveValueAppearances,
    onOpenOffPageConnector,
    onSelectFlowNode,
    onSelectNode,
  })
  const highlightedNodeIds =
    selectedNodeId && !activeNodeIds.includes(selectedNodeId)
      ? [...activeNodeIds, selectedNodeId]
      : activeNodeIds

  useEffect(() => {
    if (!isInspecting) return
    onBoxMouseLeave()
    onAliasHover(null)
    onDocReferenceHover?.(null)
  }, [isInspecting, onBoxMouseLeave, onAliasHover, onDocReferenceHover])

  return {
    nodeInspection: inspection,
    nodeOverlayLayer: {
      overlays,
      activeNodeIds: highlightedNodeIds,
      liveValueAppearances,
    },
    suspend,
    dismiss,
    isInspecting,
  }
}
